import { useEffect, useState } from "react";
import { Save, Plus, Trash2, Sliders, MapPin, Zap } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { COMBORAN_INGREDIENTS, MIX_KEYS_BY_TYPE } from "@/lib/feedModels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const num = (v) => (parseFloat(v) || 0);
const newModel = () => ({
  id: "",
  name: "",
  comboran: 50,
  type: "hijauan",
  silase_konversi: 50,
  mix: { konsentrat: 40, polar: 10, onggok: 48, premix: 1, garam: 1 },
});

export default function SettingsPage() {
  const { config, reload } = useConfig();
  const [models, setModels] = useState([]);
  const [zones, setZones] = useState([]);
  const [overhead, setOverhead] = useState([]);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    setModels((config.feed_models || []).map((m) => ({ ...m, mix: { ...m.mix } })));
    setZones([...(config.zones || [])]);
    setOverhead([...(config.overhead_components || [])]);
  }, [config]);

  const updateModel = (i, patch) =>
    setModels((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const updateMix = (i, key, val) =>
    setModels((prev) => prev.map((m, idx) => (idx === i ? { ...m, mix: { ...m.mix, [key]: val } } : m)));

  const saveModels = async () => {
    setSaving("models");
    try {
      const payload = models.map((m) => ({
        id: m.id || undefined,
        name: m.name,
        comboran: num(m.comboran),
        hijauan: 100 - num(m.comboran),
        type: m.type === "silase" ? "silase" : "hijauan",
        silase_konversi: num(m.silase_konversi) || 50,
        mix: Object.fromEntries(MIX_KEYS_BY_TYPE[m.type === "silase" ? "silase" : "hijauan"].map((k) => [k, num(m.mix[k])])),
      }));
      await api.put("/settings/feed-models", { models: payload });
      await reload();
      toast.success("Model pakan berhasil disimpan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const saveZones = async () => {
    setSaving("zones");
    try {
      await api.put("/settings/zones", { zones });
      await reload();
      toast.success("Zona berhasil disimpan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const saveOverhead = async () => {
    setSaving("overhead");
    try {
      await api.put("/settings/overhead", { components: overhead });
      await reload();
      toast.success("Komponen overhead berhasil disimpan");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const card = "bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6";

  return (
    <PageContainer title="Pengaturan" subtitle="Kelola model pakan, zona, dan komponen biaya overhead">
      <div className="space-y-6">
        {/* Model Pakan */}
        <div className={card} data-testid="settings-models">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#F7E5D2] flex items-center justify-center">
                <Sliders className="w-4 h-4 text-[#A06E4D]" />
              </div>
              <h2 className="font-display text-lg font-bold text-[#8A5230]">Model Pakan</h2>
            </div>
            <Button
              data-testid="btn-add-model"
              variant="outline"
              size="sm"
              onClick={() => setModels((p) => [...p, newModel()])}
              className="rounded-full border-[#E0A988]/70 text-[#A06E4D] hover:bg-[#F7E5D2]"
            >
              <Plus className="w-4 h-4 mr-1" /> Tambah Model
            </Button>
          </div>

          <div className="space-y-4">
            {models.map((m, i) => {
              const mixKeys = MIX_KEYS_BY_TYPE[m.type === "silase" ? "silase" : "hijauan"];
              const mixIngs = COMBORAN_INGREDIENTS.filter((ing) => mixKeys.includes(ing.id));
              const mixTotal = mixKeys.reduce((s, k) => s + num(m.mix[k]), 0);
              return (
                <div key={i} data-testid={`model-card-${i}`} className="rounded-xl border border-[#F0E3D2] p-4 bg-[#FBF3E8]/50">
                  <div className="flex items-center gap-3 mb-3">
                    <Input
                      data-testid={`model-name-${i}`}
                      placeholder="Nama model"
                      value={m.name}
                      onChange={(e) => updateModel(i, { name: e.target.value })}
                      className="flex-1 bg-white border-[#E0A988]/60 font-semibold"
                    />
                    <Button
                      data-testid={`btn-remove-model-${i}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => setModels((p) => p.filter((_, idx) => idx !== i))}
                      className="h-9 w-9 text-slate-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mb-3">
                    <Label className="text-xs text-[#7A4A28]">Persentase Comboran : {m.type === "silase" ? "Silase" : "Hijauan"}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input data-testid={`model-comboran-${i}`} type="number" value={m.comboran}
                        onChange={(e) => updateModel(i, { comboran: e.target.value })}
                        className="bg-white border-[#E0A988]/60 font-mono-num h-9 w-24" />
                      <span data-testid={`model-ratio-${i}`} className="text-sm font-semibold text-[#8A5230] font-mono-num">
                        % : {100 - num(m.comboran)}%
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs text-[#7A4A28]">Jenis Perhitungan</Label>
                      <Select value={m.type === "silase" ? "silase" : "hijauan"} onValueChange={(v) => updateModel(i, { type: v })}>
                        <SelectTrigger data-testid={`model-type-${i}`} className="bg-white border-[#E0A988]/60 h-9 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hijauan" data-testid={`model-type-${i}-hijauan`}>Comboran : Hijauan</SelectItem>
                          <SelectItem value="silase" data-testid={`model-type-${i}-silase`}>Comboran : Silase</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {m.type === "silase" && (
                      <div>
                        <Label className="text-xs text-[#7A4A28]">Konversi Silase %</Label>
                        <Input data-testid={`model-silase-konversi-${i}`} type="number" min="1" max="100" value={m.silase_konversi ?? 50}
                          onChange={(e) => updateModel(i, { silase_konversi: e.target.value })}
                          className="bg-white border-[#E0A988]/60 font-mono-num h-9 mt-1" />
                        <p className="text-[10px] text-[#A06E4D] mt-1">Silase = hijauan segar × konversi%</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-[#8A5230] mb-1">Rincian Comboran (total {Math.round(mixTotal * 100) / 100}%)</p>
                  {m.type !== "silase" && (
                    <p className="text-[10px] text-[#A06E4D] mb-2">Onggok otomatis = porsi Hijauan ({100 - num(m.comboran)}% dari total pakan)</p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {mixIngs.map((ing) => (
                      <div key={ing.id}>
                        <Label className="text-[10px] text-[#7A4A28]">{ing.label} %</Label>
                        <Input data-testid={`model-mix-${i}-${ing.id}`} type="number" value={m.mix[ing.id] ?? 0}
                          onChange={(e) => updateMix(i, ing.id, e.target.value)}
                          className="bg-white border-[#E0A988]/60 font-mono-num h-9" />
                      </div>
                    ))}
                  </div>
                  {Math.abs(mixTotal - 100) > 0.01 && (
                    <p className="text-xs text-red-600 mt-2">Total rincian comboran harus 100%.</p>
                  )}
                </div>
              );
            })}
          </div>

          <Button data-testid="btn-save-models" onClick={saveModels} disabled={saving === "models"}
            className="mt-5 rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white">
            <Save className="w-4 h-4 mr-2" /> Simpan Model Pakan
          </Button>
        </div>

        {/* Zona */}
        <ListEditor
          testid="settings-zones"
          icon={MapPin}
          title="Zona / Kandang"
          items={zones}
          setItems={setZones}
          placeholder="Nama zona (mis. Kandang Timur)"
          onSave={saveZones}
          saving={saving === "zones"}
          saveTestId="btn-save-zones"
          addTestId="btn-add-zone"
          prefix="zone"
        />

        {/* Overhead */}
        <ListEditor
          testid="settings-overhead"
          icon={Zap}
          title="Komponen Biaya Overhead"
          items={overhead}
          setItems={setOverhead}
          placeholder="Nama komponen (mis. Listrik)"
          onSave={saveOverhead}
          saving={saving === "overhead"}
          saveTestId="btn-save-overhead"
          addTestId="btn-add-overhead"
          prefix="overhead"
        />
      </div>
    </PageContainer>
  );
}

function ListEditor({ testid, icon: Icon, title, items, setItems, placeholder, onSave, saving, saveTestId, addTestId, prefix }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#F7E5D2] flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#A06E4D]" />
        </div>
        <h2 className="font-display text-lg font-bold text-[#8A5230]">{title}</h2>
      </div>
      <div className="space-y-2 mb-4">
        {items.map((val, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              data-testid={`${prefix}-input-${i}`}
              value={val}
              placeholder={placeholder}
              onChange={(e) => setItems((p) => p.map((v, idx) => (idx === i ? e.target.value : v)))}
              className="bg-[#FBF3E8] border-[#E0A988]/60"
            />
            <Button
              data-testid={`${prefix}-remove-${i}`}
              variant="ghost"
              size="icon"
              onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))}
              className="h-9 w-9 text-slate-500 hover:text-red-600 hover:bg-red-50 shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#C99A6A]">Belum ada item.</p>}
      </div>
      <div className="flex gap-2">
        <Button
          data-testid={addTestId}
          variant="outline"
          size="sm"
          onClick={() => setItems((p) => [...p, ""])}
          className="rounded-full border-[#E0A988]/70 text-[#A06E4D] hover:bg-[#F7E5D2]"
        >
          <Plus className="w-4 h-4 mr-1" /> Tambah
        </Button>
        <Button
          data-testid={saveTestId}
          onClick={onSave}
          disabled={saving}
          className="rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white"
        >
          <Save className="w-4 h-4 mr-2" /> Simpan
        </Button>
      </div>
    </div>
  );
}
